import { useCallback, useMemo } from "react";

import { useHealth } from "@/hooks/queries";
import { useAuthGate } from "@/hooks/use-auth-gate";
import { useEntitlements } from "@/hooks/use-entitlements";
import { toUserError, type UserError } from "@/lib/errors";
import { useActivity, type Tracker } from "@/stores/activity";
import {
  deriveEngineState,
  type EngineState,
  type EngineStreamSignal,
  type RequiredEntitlement,
} from "@/stores/engine";

export interface UseEngineOptions {
  /** THIS surface's own AI stream (from `useAiStream()`), if it has one.
   * There is no single global stream — e.g. two Ask tabs can stream
   * concurrently — so each caller composes its own into its own reading. */
  stream?: EngineStreamSignal;
  /** An arbitrary non-stream failure this surface wants reflected here (e.g.
   * a mutation's `onError`) — composed the same way a stream error would be.
   * Cleared automatically once you stop passing it (there's no internal
   * "stuck" state to reset). */
  error?: unknown;
  /** The entitlement the action THIS surface guards needs. When set and the
   * signed-in user lacks it, the reading becomes `needs_billing` instead of
   * whatever it would otherwise be. No-op in local mode (no billing). */
  requires?: RequiredEntitlement;
  /** What `retry()` should do for a `failed`/`partial` reading — e.g. re-run
   * the mutation or restart the stream with its original args. Ignored for
   * `unavailable` (which always re-checks health) and `needs_auth` (which
   * always reloads); there is nothing generic to retry for `needs_billing` /
   * `needs_permission` — render `RestrictedState` for those instead. */
  onRetry?: () => void;
}

export interface EngineReading {
  state: EngineState;
  /** Trackers currently queued or running — NOT the full job history. A
   * tracker that finished (`done`/`failed`) stops counting here the moment it
   * settles; a stale failure from ten minutes ago must never pin the whole
   * studio's engine reading at `failed`. Per-item failures belong on the item
   * itself (a file row's status badge, a document's `error` field), not here. */
  activeJobs: Tracker[];
  lastError: UserError | null;
  requiredEntitlement?: RequiredEntitlement;
  retry: () => void;
}

/** The single source of truth for "is the engine busy / available / gated."
 * Composes health (unavailable/initializing/ready), this surface's AI stream
 * if any, background jobs, the auth gate, and entitlements into ONE
 * {@link EngineState} — see `stores/engine.ts` for the precedence rules.
 * Later surfaces should read this rather than re-deriving any piece of it. */
export function useEngine(options: UseEngineOptions = {}): EngineReading {
  const health = useHealth();
  const auth = useAuthGate();
  const entitlements = useEntitlements();
  const trackers = useActivity((s) => s.trackers);

  const activeJobs = useMemo(
    () => trackers.filter((t) => t.status === "running" || t.status === "queued"),
    [trackers],
  );

  const healthState = health.isLoading ? "loading" : health.isError ? "unavailable" : "ok";
  const authState =
    auth.status === "loading" ? "loading" : auth.status === "signed-out" ? "signed-out" : "resolved";

  const entitlementInput = options.requires
    ? {
        requires: options.requires,
        loading: entitlements.loading,
        satisfied:
          options.requires === "models_premium"
            ? entitlements.modelsPremium
            : entitlements.storageUnlimited,
      }
    : null;

  const derivation = deriveEngineState({
    health: healthState,
    auth: authState,
    entitlement: entitlementInput,
    stream: options.stream,
    hasReportedError: options.error != null,
    jobs: activeJobs,
  });

  const streamErrorText = options.stream?.error;
  const reportedError = options.error;
  const healthError = health.error;

  const lastError = useMemo<UserError | null>(() => {
    if (derivation.state === "unavailable") return toUserError(healthError);
    if (derivation.failureSource === "stream") {
      return toUserError(new Error(streamErrorText || "The AI response failed."));
    }
    if (derivation.failureSource === "reported") return toUserError(reportedError);
    return null;
  }, [derivation.state, derivation.failureSource, healthError, streamErrorText, reportedError]);

  const refetchHealth = health.refetch;
  const onRetry = options.onRetry;
  const state = derivation.state;

  const retry = useCallback(() => {
    if (state === "unavailable") {
      void refetchHealth();
      return;
    }
    if (state === "needs_auth") {
      // Sign-in is a full-page gate in this app (see use-auth-gate.ts), not a
      // modal we can pop from anywhere — a reload is the one recovery that's
      // always safe and always available. Local-first storage means server
      // state is never lost; the editor's autosave/keepalive paths already
      // guard unsaved in-memory edits.
      window.location.reload();
      return;
    }
    if (state === "failed" || state === "partial") {
      onRetry?.();
    }
    // needs_billing / needs_permission: nothing generic to retry — the
    // caller should render RestrictedState, which has its own dedicated CTA.
  }, [state, refetchHealth, onRetry]);

  return {
    state,
    activeJobs,
    lastError,
    requiredEntitlement: derivation.requiredEntitlement,
    retry,
  };
}
